/** @file extentPx: pixels from a pick's pivot to the tile border along the drawn bone direction. */
import type { FacetPick } from '@zilo/pose3d-facet/contract';

/** Degrees to radians. */
const RAD = Math.PI / 180;

/** The positive travel along one axis from `at` to the border `[0, size]` in direction `d` (Infinity when d is 0). */
function travel(at: number, size: number, d: number): number {
  if (d > 1e-9) return (size - at) / d;
  if (d < -1e-9) return at / -d;
  return Number.POSITIVE_INFINITY;
}

/**
 * `drawnScreenAngleDeg` is 0 when the bone points down the image and 90 when it points right (`flip` mirrors x);
 * the result is the distance from `pivot` to the tile border that way. 0 when the pivot lies outside the tile.
 */
export function extentPx(pick: Pick<FacetPick, 'rect' | 'pivot' | 'flip' | 'drawnScreenAngleDeg'>): number {
  const w = pick.rect[2];
  const h = pick.rect[3];
  const a = pick.drawnScreenAngleDeg * RAD;
  const [px, py] = pick.pivot;
  if (px < 0 || py < 0 || px > w || py > h) return 0;
  const t = Math.min(travel(px, w, (pick.flip ? -1 : 1) * Math.sin(a)), travel(py, h, Math.cos(a)));
  return Number.isFinite(t) ? Math.max(0, t) : 0;
}
